/**
 * useEntitlements — Hook for feature gating based on the company's active plan.
 *
 * Reads entitlements from the sidebar data that AdminApp already fetches.
 * Pass the sidebarData object (or just its entitlements map) to the hook.
 *
 * Usage:
 *   const { canAccess, getValue } = useEntitlements(sidebarData);
 *   if (canAccess('whatsapp_broadcast')) { ... }
 *   const kbLevel = getValue('kb_training'); // 'basic' | 'advanced' | 'custom' | null
 */

import { useMemo } from 'react';

export default function useEntitlements(sidebarData) {
  const entitlements = useMemo(
    () => (sidebarData?.entitlements) || {},
    [sidebarData?.entitlements],
  );

  const planId = sidebarData?.plan_id || 'free';

  /**
   * Check if the current plan allows a feature.
   * @param {string} feature - feature key (e.g. 'whatsapp_broadcast')
   * @returns {boolean}
   */
  const canAccess = (feature) => {
    if (!feature) return false;
    // Entitlements map: { feature_key: true/false }
    // or { feature_key: { allowed: bool, value: string } }
    const val = entitlements[feature];
    if (typeof val === 'boolean') return val;
    if (val && typeof val === 'object') return !!val.allowed;
    return false;
  };

  /**
   * Get the value of a non-boolean entitlement (e.g. 'kb_training' → 'advanced').
   * @param {string} feature
   * @returns {string|null}
   */
  const getValue = (feature) => {
    const val = entitlements[feature];
    if (val && typeof val === 'object') return val.value || null;
    return null;
  };

  return { canAccess, getValue, planId, entitlements };
}
